<template>
  <div>
    <div v-if="current" class="flex-row img-detail">
      <div class="flex-column flex-center">
        <i
          @click="imageIndex = (media.length - 1 + imageIndex) % media.length"
          class="pi pi-chevron-left"
        />
      </div>
      <div class="main-image flex-stretch">
        <div v-if="current.type === 'video'" class="main video-frame">
          <iframe
            v-if="/player.vimeo.com/.test(current.source)"
            :src="current.source"
            frameborder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowfullscreen
          />
          <video v-else :src="current.source" controls autoplay />
        </div>
        <ImageZoomer
          v-else-if="current.source && props.zoom"
          @contextmenu="onImageRightClick"
          :image="current.source.w500"
          :zoomed-image="current.source.original"
          :alt="props.alt"
          class="main"
          ref="main"
          aria-haspopup="true"
        />
        <ThumbnailedImage
          v-else-if="current.source"
          :images="current.source"
          :alt="props.alt"
          class="main"
        />
      </div>
      <div class="flex-column flex-center">
        <i
          @click="imageIndex = (imageIndex + 1) % media.length"
          class="pi pi-chevron-right"
        />
      </div>
    </div>
    <div v-if="props.includeSmall" class="small flex-row">
      <template v-for="(group, index) of media">
        <img
          v-if="group.thumb"
          v-bind="group.thumb"
          @click="imageIndex = index"
          class="video-thumbnail"
        />
        <div
          v-else-if="group.type === 'video'"
          @click="imageIndex = index"
          class="small-frame"
        >
          <i class="pi pi-video" />
        </div>
        <ThumbnailedImage
          v-else
          @click="imageIndex = index"
          @contextmenu="onImageRightClick($event, index)"
          :images="group.source"
          :alt="props.alt"
          size="72px"
          class="thumbnail"
        />
      </template>
      <ContextMenu
        ref="menu"
        :model="[
          { label: 'Download', icon: 'pi pi-copy', command: performDownload },
        ]"
      />

      <div class="image-button">
        <DownloadButton
          :images="media.map((i) => i.source)"
          :download="props.download"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ProductVideo } from "@/stores/defs/shop_defs";
import { download } from "@/utils/download";
import { extname } from "@paparazzi/components/mixins/globals";
import { expandThumb } from "paparazzi/components/stores/thumbnail_conversion";
import ContextMenu from "primevue/contextmenu";
import { computed, ref, watch } from "vue";
import ThumbnailedImage from "../ThumbnailedImage.vue";
import DownloadButton from "./DownloadButton.vue";
import ImageZoomer from "./ImageZoomer.vue";

const props = defineProps<{
  images: any[];
  videos?: ProductVideo[];
  alt: string;
  download?: string;
  video?: { source: string; thumb: any };
  includeSmall?: boolean;
  zoom?: boolean;
}>();
// const videoThumbs = ref<Record<string, string>>({});

const menu = ref<ContextMenu>();
const imageIndex = ref(0);
const media = computed<{ type: string; source: any; thumb: any }[]>(() => {
  return [
    ...props.images.map((i) => {
      const retval: { [key: string]: string } = {};
      for (const key in i) {
        retval[key] = expandThumb(i[key]);
      }
      return { type: "image", source: retval };
    }),
    // ...(props.videos
    //   ? props.videos.map((v) => ({
    //       type: "video",
    //       source: v.url,
    //     }))
    //   : []),
    ...(props.video ? [{ type: "video", ...props.video }] : []),
  ];
});

const current = computed(() => {
  return media.value[imageIndex.value];
});

watch(media, () => {
  imageIndex.value = 0;
});

// onMounted(async () => {
//   if (props.videos) {
//     props.videos.forEach(async (video) => {
//       if (!videoThumbs.value[video.url]) {
//         const response = await fetch(
//           `https://vimeo.com/api/oembed.json?url=${video.url}`,
//         );
//         const json = await response.json();
//         // videoThumbs.value[video.url] = json["thumbnail_url_with_play_button"];
//         videoThumbs.value[video.url] = json["thumbnail_url"];
//       }
//     });
//   }
// });

function onImageRightClick(event: MouseEvent, index?: number) {
  if (index !== undefined) {
    imageIndex.value = index;
  }
  if (menu.value) {
    menu.value.show(event);
  }
}

function performDownload() {
  const image = props.images[imageIndex.value];
  const links = [
    {
      filename: `${props.download}-${imageIndex.value}.${extname(image.original)}`,
      url: expandThumb(image.original),
    },
  ];
  download(links);
}
</script>

<style scoped>
.img-detail {
  width: 394px;
  max-width: 100%;
}
.img-detail .pi.pi-chevron-left,
.img-detail .pi.pi-chevron-right {
  font-size: 12px;
  padding: 6px;
  cursor: pointer;
}
.small {
  gap: 24px;
  margin-top: var(--Sm, 24px);
  max-width: 346px;
  overflow: hidden;
  position: relative;
}
.video-thumbnail,
.thumbnail {
  cursor: pointer;
}
.small .image-button {
  opacity: 0.5;
  margin-left: auto;
}
.small > .video-thumbnail {
  display: grid;
  align-items: center;
  text-align: center;
  justify-content: center;
  flex: 1 1 calc(100% / 3);
  max-width: calc(100% / 3);
  max-width: 72px;
  object-fit: cover;
  aspect-ratio: 8 / 10;
}

.small > img {
  flex: 1 1 calc(100% / 3);
  max-width: calc(100% / 3);
  max-width: 72px;
  min-width: 20px;
  aspect-ratio: 8 / 10;
}

.main > iframe,
.main > video,
.main-image > img {
  width: 100%;
  height: auto;
  aspect-ratio: 8 / 10;
  object-fit: contain;
}

.main-image {
  max-width: 346px;
  aspect-ratio: 8 / 10;
  display: flex;
  justify-content: center;
  align-items: center;
}

.video-frame {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: auto;
  aspect-ratio: 8 / 10;
}

.small-frame {
  width: 60px;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  i {
    font-size: 2em;
  }
}
video {
  width: 100%;
}

@media screen and (max-width: 800px) {
  .main-image > img {
    width: 345px;
  }
}
</style>
